import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import {
  AppstoreOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  NodeIndexOutlined,
  PartitionOutlined,
  RedoOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  ApiService,
  ProcessSection,
  ProcessStatus,
  ProcessWorkflow,
  TemplateProcess,
} from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import ProcessOutline, { ItemKind } from "../../components/process/ProcessOutline";
import ProcessCanvas from "../../components/process/canvas/ProcessCanvas";
import ItemTray from "../../components/process/canvas/ItemTray";
import StepInspector from "../../components/process/StepInspector";
import LifecycleEditor from "../../components/process/LifecycleEditor";
import type { TransitionDraft } from "../../components/process/TransitionRules";
import StatusBar from "../../components/process/StatusBar";
import TryItBar from "../../components/process/TryItBar";
import PresetPicker from "../../components/process/PresetPicker";
import CreateSectionModal from "../../components/process/CreateSectionModal";
import ProcessHistoryDrawer from "../../components/process/ProcessHistoryDrawer";
import { caseFieldRefs } from "../../components/process/types";
import type { FieldRef, Selection, StageDraft } from "../../components/process/types";
import { validate } from "../../components/process/validation";
import { useProcessDraft, type Draft } from "../../components/process/useProcessDraft";
import { looseItems } from "../../components/process/canvas/layout";
import type { Preset } from "../../components/process/presets";
import { useFillViewport } from "../../components/process/useFillViewport";
import { useTabTitle } from "../../tabs/tabContext";

const { Title } = Typography;

/** Local key for a step the server has not seen yet. */
let newStageCounter = 0;
const nextKey = () => `new-${++newStageCounter}`;

const toDraft = (process: TemplateProcess): Draft => ({
  stages: process.stages.map((s) => ({
    id: s.id,
    key: String(s.id),
    name: s.name,
    kind: s.kind,
    condition: s.condition ?? {},
    sla_hours: s.sla_hours ?? null,
    form_ids: [...s.form_ids],
    workflow_ids: [...s.workflow_ids],
  })),
  sections: Object.fromEntries(process.sections.map((s) => [s.id, { ...s }])),
  transitions: (process.transitions ?? []).map((t) => ({ ...t })),
  workflows: process.workflows.map((w) => ({
    ...w,
    roles: w.roles.map((r) => ({ ...r })),
  })),
  statuses: process.statuses.map((s) => ({ ...s })),
});

/**
 * The Process Designer.
 *
 * Every process setting used to be reachable only through Django admin, so a
 * client administrator could not change their own process. This edits the whole
 * layout as one document and saves it in one call: reordering steps, moving a
 * section between them and renumbering what is left are a single user action,
 * and a half-applied process is worse than none.
 *
 * **The canvas is the editor**, not a picture of it. Steps are inserted between
 * their neighbours, dragged into a new order, filled by dropping sections onto
 * them, and opened by clicking. The previous version led with a text outline
 * and hid the diagram behind an icon, which meant the first question anybody
 * asks -- "what does this process look like?" -- was answered by the surface
 * nobody was looking at. The outline survives as a second reading: it is better
 * for a very long process and it is the keyboard-accessible one.
 *
 * Saving and publishing are separate on purpose. Saving stores the layout; only
 * publishing makes it reach new cases, and cases already in flight keep the
 * version they started on either way.
 */
const ProcessDesignerPage = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useParams();
  const templateId = Number(id);

  const [process, setProcess] = useState<TemplateProcess | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [conflict, setConflict] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [tryItOpen, setTryItOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [newSectionFor, setNewSectionFor] = useState<{ stageKey: string | null } | null>(
    null
  );
  const [selection, setSelection] = useState<Selection>(null);
  const [denied, setDenied] = useState(false);
  const [view, setView] = useState<"canvas" | "outline">("canvas");
  const [simulation, setSimulation] = useState<Record<string, boolean> | null>(null);
  const [trayDragging, setTrayDragging] = useState<
    { kind: "form" | "workflow"; id: number } | null
  >(null);

  const { draft, edit, reset, undo, redo, canUndo, canRedo, dirty } =
    useProcessDraft();
  // The editor takes whatever is left below the toolbar, measured rather than
  // guessed — see useFillViewport.
  const { ref: editorRef, height: editorHeight } = useFillViewport();
  const loaded = useRef(false);
  // Read by the keyboard handler, which is bound once — putting `selection` in
  // its dependency list would rebind a window listener on every click.
  const selectionRef = useRef<Selection>(null);
  const removeStageRef = useRef<(key: string) => void>(() => {});

  useTabTitle(process?.template?.short_name);

  const apply = useCallback(
    (data: TemplateProcess) => {
      setProcess(data);
      reset(toDraft(data));
      setProblems([]);
      setConflict("");
      loaded.current = true;
    },
    [reset]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      apply(await ApiService.apiMainTemplateProcessRetrieve(templateId));
    } catch (e: any) {
      if (e?.status === 403) setDenied(true);
      else message.error(apiErrorMessage(e, t("Something went wrong")));
    }
    setLoading(false);
  }, [templateId, apply, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the selection pointing at something that still exists — a reload or a
  // delete can pull it out from under the inspector.
  useEffect(() => {
    if (!selection || !loaded.current) return;
    const alive =
      selection.type === "stage"
        ? draft.stages.some((s) => s.key === selection.key)
        : selection.type === "section"
        ? !!draft.sections[selection.id]
        : draft.workflows.some((w) => w.id === selection.id);
    if (!alive) setSelection(null);
  }, [draft, selection]);

  // Warn before losing edits — this page holds the only copy until Save.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Keyboard: undo/redo, and Delete to remove the selected step.
  //
  // Everything here is ignored while a text field has focus — undo must still
  // mean "undo my typing", and Delete must still mean "delete a character".
  // Removing a step from the keyboard is only safe *because* undo exists;
  // without it an accidental keypress would be unrecoverable damage.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const typing =
        tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable;
      if (typing) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }

      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectionRef.current?.type === "stage"
      ) {
        e.preventDefault();
        removeStageRef.current(selectionRef.current.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // --- draft edits ------------------------------------------------------- //
  const patchStage = (
    key: string,
    patch: Partial<StageDraft>,
    merge?: string
  ) =>
    edit(
      (d) => {
        const stage = d.stages.find((s) => s.key === key);
        if (stage) Object.assign(stage, patch);
      },
      "Change a step",
      merge
    );

  const moveStage = (key: string, delta: number) =>
    edit((d) => {
      const i = d.stages.findIndex((s) => s.key === key);
      const target = i + delta;
      if (i < 0 || target < 0 || target >= d.stages.length) return;
      [d.stages[i], d.stages[target]] = [d.stages[target], d.stages[i]];
    }, "Move a step");

  const removeStage = (key: string) =>
    edit((d) => {
      d.stages = d.stages.filter((s) => s.key !== key);
    }, "Remove a step");

  selectionRef.current = selection;
  removeStageRef.current = removeStage;

  /** Insert a step at a position — the `+` between two nodes lands here. */
  const addStage = (kind: string, atIndex: number) => {
    const key = nextKey();
    edit((d) => {
      const stage: StageDraft = {
        id: null,
        key,
        name: "",
        kind,
        condition: {},
        sla_hours: null,
        form_ids: [],
        workflow_ids: [],
      };
      const at = Math.max(0, Math.min(atIndex, d.stages.length));
      d.stages.splice(at, 0, stage);
    }, "Add a step");
    setSelection({ type: "stage", key });
  };

  const reorderStages = (from: number, to: number) =>
    edit((d) => {
      const [moved] = d.stages.splice(from, 1);
      d.stages.splice(to, 0, moved);
    }, "Reorder the steps");

  /**
   * Put a section or sign-off in a step, at a position — or nowhere.
   *
   * Moving between steps, reordering inside one and taking something out are
   * the same gesture to someone dragging, so they are one operation here. An
   * index below zero means "at the end".
   */
  const placeItem = (
    kind: ItemKind,
    itemId: number,
    toStageKey: string | null,
    index: number
  ) =>
    edit((d) => {
      const field = kind === "form" ? "form_ids" : "workflow_ids";
      d.stages.forEach((s) => {
        s[field] = s[field].filter((x) => x !== itemId);
      });
      if (!toStageKey) return;
      const target = d.stages.find((s) => s.key === toStageKey);
      if (!target) return;
      if (index < 0 || index > target[field].length) target[field].push(itemId);
      else target[field].splice(index, 0, itemId);
    }, "Move something between steps");

  const patchSection = (
    formId: number,
    patch: Partial<ProcessSection>,
    merge?: string
  ) =>
    edit(
      (d) => {
        d.sections[formId] = { ...d.sections[formId], ...patch };
      },
      "Change a section",
      merge
    );

  const patchWorkflow = (workflowId: number, patch: Partial<ProcessWorkflow>) =>
    edit((d) => {
      d.workflows = d.workflows.map((w) =>
        w.id === workflowId ? { ...w, ...patch } : w
      );
    }, "Change a sign-off");

  const removeWorkflow = (workflowId: number) =>
    edit((d) => {
      d.workflows = d.workflows.filter((w) => w.id !== workflowId);
      d.stages.forEach((s) => {
        s.workflow_ids = s.workflow_ids.filter((x) => x !== workflowId);
      });
    }, "Remove a sign-off");

  const addWorkflow = (stageKey?: string) => {
    // A negative id is a handle for a row that does not exist yet, so a step
    // can place it in the same save that creates it.
    const handle = Math.min(0, ...draft.workflows.map((w) => w.id ?? 0)) - 1;
    edit((d) => {
      d.workflows.push({
        id: handle,
        name: "",
        type: "Button",
        approvers_count: 1,
        roles: [],
      });
      const stage = d.stages.find((s) => s.key === stageKey);
      if (stage) stage.workflow_ids.push(handle);
    }, "Add a sign-off");
    setSelection({ type: "workflow", id: handle });
  };

  const setStatuses = (statuses: ProcessStatus[]) =>
    edit((d) => {
      d.statuses = statuses;
      // A restriction on a move that no longer exists is dead configuration
      // that reads as a live one -- and the server refuses to save it. Dropped
      // here so removing a move never puts the whole process into a state that
      // cannot be saved, with the reason two drawers away.
      const edges = new Set(
        statuses.flatMap((st) =>
          (st.next_possible_statuses ?? []).map((to) => `${st.id}->${to}`)
        )
      );
      d.transitions = d.transitions.filter((r) =>
        edges.has(`${r.from_status}->${r.to_status}`)
      );
    }, "Change the lifecycle");

  const setTransitions = (transitions: TransitionDraft[]) =>
    edit((d) => {
      d.transitions = transitions;
    }, "Change who can make a move");

  /** Add a preset's spine to whatever is already there. */
  const applyPreset = (preset: Preset) =>
    edit((d) => {
      preset.stages.forEach((stage) => {
        d.stages.push({
          id: null,
          key: nextKey(),
          name: t(stage.name),
          kind: stage.kind,
          condition: {},
          sla_hours: null,
          form_ids: [],
          workflow_ids: [],
        });
      });
    }, "Start from a shape");

  /** A section just created by the modal, dropped straight into its step. */
  const acceptNewSection = (
    section: { id: number; title: string },
    stageKey: string | null
  ) => {
    edit((d) => {
      // Only the process properties live here; the schema belongs to the form
      // designer. Defaults match what the server created it with.
      d.sections[section.id] = {
        id: section.id,
        title: section.title,
        type: "Dynamic",
        order: Object.keys(d.sections).length + 1,
        stage: null,
        fill_permission: "Anyone",
        fill_group: null,
        fill_group_name: null,
        is_required: true,
        signature_kind: "NotRequired",
        condition: {},
        condition_text: "",
        field_names: [],
        fields: [],
      };
      const stage = d.stages.find((s) => s.key === stageKey);
      if (stage && stage.kind === "DataEntry") stage.form_ids.push(section.id);
    }, "Add a section");
  };

  // --- save / publish ---------------------------------------------------- //
  const payload = (publish: boolean) => ({
    stages: draft.stages.map((s) => ({
      id: s.id,
      name: s.name,
      kind: s.kind,
      condition: s.condition,
      sla_hours: s.sla_hours ?? null,
      form_ids: s.form_ids,
      workflow_ids: s.workflow_ids,
    })),
    sections: Object.values(draft.sections).map((s) => ({
      id: s.id,
      title: s.title,
      fill_permission: s.fill_permission,
      fill_group: s.fill_group,
      is_required: s.is_required,
      signature_kind: s.signature_kind,
      condition: s.condition ?? {},
    })),
    workflows: draft.workflows.map((w) => ({
      id: w.id,
      name: w.name,
      type: w.type,
      approvers_count: w.approvers_count,
      roles: w.roles.map((r) => ({
        id: r.id,
        title: r.title,
        assignment: r.assignment,
        pool: r.pool,
        quorum: r.quorum,
        condition: r.condition ?? {},
      })),
    })),
    // Only the moves that carry a restriction; the rest need no row (see
    // TransitionRules). Sent even when empty, so clearing the last one clears
    // it on the server -- omitting the key means "an older client, leave them".
    transitions: draft.transitions.map((t) => ({
      from_status: t.from_status,
      to_status: t.to_status,
      condition: t.condition ?? {},
      allowed_groups: t.allowed_groups ?? [],
    })),
    statuses: draft.statuses.map((s) => ({
      id: s.id,
      name: s.name,
      kind: s.kind,
      action_title: s.action_title,
      system_actions: s.system_actions,
      next_auto_status: s.next_auto_status,
      next_possible_statuses: s.next_possible_statuses,
    })),
    publish,
    // What we last read. If someone else has saved since, the server refuses
    // rather than letting this overwrite their work.
    base_revision: process?.revision,
  });

  const save = async (publish: boolean) => {
    setSaving(true);
    setProblems([]);
    try {
      apply(
        await ApiService.apiMainTemplateProcessUpdate(templateId, payload(publish))
      );
      message.success(
        publish
          ? t("Published — new cases will use this process")
          : t("Process saved")
      );
    } catch (e: any) {
      const list = e?.body?.problems;
      if (e?.status === 409) {
        // Someone else saved while this page was open. Never merge silently.
        setConflict(e?.body?.message ?? t("Someone else changed this process."));
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (Array.isArray(list) && list.length) {
        setProblems(list);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        message.error(apiErrorMessage(e, t("Something went wrong")));
      }
    }
    setSaving(false);
  };

  const publishOnly = async () => {
    setSaving(true);
    try {
      apply(await ApiService.apiMainTemplateProcessPublishCreate(templateId));
      message.success(t("Published — new cases will use this process"));
    } catch (e: any) {
      message.error(apiErrorMessage(e, t("Something went wrong")));
    }
    setSaving(false);
  };

  // Every field in the template, tagged with the section that asks it and the
  // step that section sits in. The step is what lets the condition builder
  // refuse a rule about an answer the case has not given yet.
  const fields: FieldRef[] = useMemo(() => {
    if (!process) return [];
    const stageIndexOfSection = new Map<number, number>();
    draft.stages.forEach((stage, index) =>
      stage.form_ids.forEach((formId) => stageIndexOfSection.set(formId, index))
    );
    return [
      ...process.sections.flatMap((section) =>
        (section.fields ?? [])
          // A rule can be written against anything in this list, so anything a
          // rule could never satisfy must not be in it: a guidance block stores
          // no answer, and a formatted description is prose. `!== false` rather
          // than a truthy test -- an older server sends no flag at all, and a
          // field this client cannot classify stays offered.
          .filter((field) => field.testable !== false)
          .map((field) => ({
          ...field,
            sectionId: section.id,
            sectionTitle: section.title,
            stageIndex: stageIndexOfSection.get(section.id) ?? -1,
          }))
      ),
      // The case itself, listed last so the form's own questions come first.
      // Folded in here rather than inside the condition builder so the
      // validator and "Try it" see the same list — otherwise a rule about
      // `case.start_date` would be reported as a rule about a field no section
      // asks, which is exactly backwards.
      ...caseFieldRefs(process.choices.condition_case_fields ?? []),
    ];
  }, [process, draft.stages]);

  // Found in the browser, against the draft. Distinct from `problems`, which is
  // what the server said when a save was refused.
  const draftProblems = useMemo(
    () =>
      validate({
        stages: draft.stages,
        sections: draft.sections,
        workflows: draft.workflows,
        statuses: draft.statuses,
        fields,
      }),
    [draft, fields]
  );
  const errorCount = draftProblems.filter((p) => p.severity === "error").length;

  const loose = useMemo(
    () => looseItems(draft.stages, draft.sections, draft.workflows),
    [draft]
  );

  if (denied) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("You do not have permission to change this template's process.")}
      />
    );
  }
  if (loading || !process) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Toolbar. Sticky so Save / Publish and the state chips never scroll away. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          padding: "2px 0 8px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Title level={5} style={{ margin: 0 }} ellipsis>
            {process.template.name}
          </Title>
          <Space size={4} wrap>
            {process.published ? (
              <Tag bordered={false}>
                {t("Published v{{n}}", { n: process.published.version })}
              </Tag>
            ) : (
              <Tag color="warning">{t("Never published")}</Tag>
            )}
            {dirty && <Tag color="processing">{t("Unsaved changes")}</Tag>}
            {!dirty && process.has_unpublished_changes && (
              <Tag color="warning">{t("Saved, not published")}</Tag>
            )}
          </Space>
        </div>

        <StatusBar
          problems={draftProblems}
          impact={process.impact}
          hasUnpublishedChanges={process.has_unpublished_changes}
          pendingChanges={process.pending_changes}
          onSelect={setSelection}
          onOpenLifecycle={() => setLifecycleOpen(true)}
        />

        <div style={{ flex: 1 }} />

        <Space size={4} wrap>
          <Space.Compact>
            <Tooltip title={t("Undo") + " (Ctrl+Z)"}>
              <Button
                icon={<UndoOutlined />}
                disabled={!canUndo}
                onClick={undo}
                aria-label={t("Undo")}
              />
            </Tooltip>
            <Tooltip title={t("Redo") + " (Ctrl+Shift+Z)"}>
              <Button
                icon={<RedoOutlined />}
                disabled={!canRedo}
                onClick={redo}
                aria-label={t("Redo")}
              />
            </Tooltip>
          </Space.Compact>

          {/* Labelled, not icon-only. Two unlabelled icons give no clue which
              view you are already in, so switching to the one you are on looks
              like a dead button — especially on a template with no steps,
              where both views are empty and look identical. */}
          <Segmented
            value={view}
            onChange={(v) => setView(v as "canvas" | "outline")}
            options={[
              {
                value: "canvas",
                label: t("Canvas"),
                icon: <PartitionOutlined />,
              },
              {
                value: "outline",
                label: t("List"),
                icon: <UnorderedListOutlined />,
              },
            ]}
          />

          <Tooltip title={t("Start from a ready-made shape")}>
            <Button
              icon={<AppstoreOutlined />}
              onClick={() => setPresetsOpen(true)}
            >
              {t("Shapes")}
            </Button>
          </Tooltip>

          <Button
            type={tryItOpen ? "primary" : "default"}
            ghost={tryItOpen}
            icon={<ExperimentOutlined />}
            onClick={() => setTryItOpen((open) => !open)}
          >
            {t("Try it")}
          </Button>

          <Button
            icon={<NodeIndexOutlined />}
            onClick={() => setLifecycleOpen(true)}
          >
            {t("States")}
          </Button>

          <Tooltip title={t("History")}>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOpen(true)}
              aria-label={t("History")}
            />
          </Tooltip>

          <Button disabled={!dirty || saving} onClick={load}>
            {t("Discard")}
          </Button>
          <Button loading={saving} disabled={!dirty} onClick={() => save(false)}>
            {t("Save")}
          </Button>
          {/* Publishing with known errors would only be refused by the server,
              so the block happens here where the reason is. */}
          <Badge count={errorCount} offset={[-4, 2]}>
            <Button
              type="primary"
              loading={saving}
              disabled={
                errorCount > 0 || (!dirty && !process.has_unpublished_changes)
              }
              onClick={() => (dirty ? save(true) : publishOnly())}
            >
              {t("Publish")}
            </Button>
          </Badge>
        </Space>
      </div>

      {conflict && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={t("This process was changed by someone else")}
          description={conflict}
          action={
            <Button size="small" onClick={load}>
              {t("Reload")}
            </Button>
          }
        />
      )}

      {problems.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 8 }}
          message={t("This process cannot be saved yet")}
          description={
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          }
        />
      )}

      {/* The editor: tray, canvas, and the inspector floating over it. */}
      <div
        ref={editorRef as any}
        style={{
          height: editorHeight,
          minHeight: 0,
          marginTop: 8,
          display: "flex",
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          overflow: "hidden",
          background: token.colorBgContainer,
        }}
      >
        <div style={{ width: 210, flex: "0 0 auto", minHeight: 0 }}>
          <ItemTray
            sections={loose.sections}
            workflows={loose.workflows}
            onSelect={setSelection}
            setDragging={setTrayDragging}
            onCreateSection={() => setNewSectionFor({ stageKey: null })}
            onAddWorkflow={() => addWorkflow(undefined)}
          />
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tryItOpen && (
            <TryItBar
              templateId={templateId}
              fields={fields}
              stages={draft.stages}
              sections={Object.values(draft.sections)}
              workflows={draft.workflows}
              onResult={setSimulation}
              onClose={() => setTryItOpen(false)}
            />
          )}

          <div style={{ flex: 1, minHeight: 0, overflow: view === "outline" ? "auto" : "hidden" }}>
            {view === "canvas" ? (
              <ProcessCanvas
                key={trayDragging ? "dragging" : "idle"}
                stages={draft.stages}
                sections={draft.sections}
                workflows={draft.workflows}
                statuses={draft.statuses}
                fields={fields}
                problems={draftProblems}
                simulation={simulation}
                selection={selection}
                onSelect={setSelection}
                onReorderStages={reorderStages}
                onPlaceItem={placeItem}
                onAddStage={addStage}
                onRemoveStage={removeStage}
                onAddWorkflow={addWorkflow}
                onCreateSection={(stageKey) => setNewSectionFor({ stageKey })}
                onOpenPresets={() => setPresetsOpen(true)}
              />
            ) : (
              <div style={{ padding: 12 }}>
                <ProcessOutline
                  stages={draft.stages}
                  sections={draft.sections}
                  workflows={draft.workflows}
                  statuses={draft.statuses}
                  selection={selection}
                  onSelect={setSelection}
                  onPlaceItem={placeItem}
                  onReorderStages={reorderStages}
                  onAddStage={(kind) => addStage(kind, draft.stages.length)}
                  onOpenLifecycle={() => setLifecycleOpen(true)}
                  fields={fields}
                  problems={draftProblems}
                  simulation={simulation}
                />
              </div>
            )}
          </div>
        </div>

        {/* The inspector sits *beside* the canvas, not over it.
            Editing has to stay next to the thing being edited -- adding a rule
            immediately draws its bypass, and that link is the whole point of a
            canvas -- but floating covered the nodes nearest the panel, which on
            a step-heavy process meant covering the one you had just clicked.
            Docking keeps the adjacency and gives the canvas back its width;
            the canvas re-measures, so the lane bands refill the space. */}
        {selection && (
          <div
            style={{
              width: 380,
              flex: "0 0 auto",
              overflowY: "auto",
              padding: 14,
              borderLeft: `1px solid ${token.colorBorderSecondary}`,
              background: token.colorBgContainer,
            }}
          >
            <StepInspector
              selection={selection}
              stages={draft.stages}
              sections={draft.sections}
              workflows={draft.workflows}
              groups={process.groups}
              orgUnits={process.org_units}
              choices={process.choices}
              fields={fields}
              problems={draftProblems}
              onClose={() => setSelection(null)}
              onStageChange={patchStage}
              onStageMove={moveStage}
              onStageRemove={removeStage}
              onSectionChange={patchSection}
              onWorkflowChange={patchWorkflow}
              onWorkflowRemove={removeWorkflow}
              onAddWorkflow={addWorkflow}
              onCreateSection={(stageKey) => setNewSectionFor({ stageKey })}
            />
          </div>
        )}
      </div>

      <Drawer
        title={t("The states a case moves through")}
        width={Math.min(880, window.innerWidth - 80)}
        open={lifecycleOpen}
        onClose={() => setLifecycleOpen(false)}
      >
        <LifecycleEditor
          statuses={draft.statuses}
          onChange={setStatuses}
          statusKinds={process.choices.status_kinds}
          systemActions={process.choices.system_actions}
          transitions={draft.transitions}
          onTransitionsChange={setTransitions}
          groups={process.groups}
          fields={fields}
          conditionOps={process.choices.condition_ops}
          conditionOrderOps={process.choices.condition_order_ops}
        />
      </Drawer>

      <PresetPicker
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        onPick={applyPreset}
        hasStages={draft.stages.length > 0}
      />

      <CreateSectionModal
        open={newSectionFor !== null}
        templateId={templateId}
        stageKey={newSectionFor?.stageKey ?? null}
        onClose={() => setNewSectionFor(null)}
        onCreated={acceptNewSection}
      />

      <ProcessHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        templateId={templateId}
        revision={process.revision}
        onRestored={apply}
      />
    </div>
  );
};

export default ProcessDesignerPage;
